import React, { useState, useEffect } from 'react';
import { Menu, X, Wrench, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Handle hash navigation from other pages
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Check if we're on the homepage
    if (window.location.pathname === '/') {
      // If on homepage, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } else {
      // If on other pages, navigate to homepage then scroll
      window.location.href = `/#${sectionId}`;
      setIsMobileMenuOpen(false);
    }
  };

  const navigateToPage = (url) => {
    window.location.href = url;
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  };

  return (
    <header 
      className={`fixed top-12 md:top-16 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/glpenkiu_logo.png" 
              alt="Think Mechanic Logo" 
              className="h-14 md:h-16 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium"
            >
              About Us
            </button>
            
            {/* Services Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                onClick={() => scrollToSection('services')}
                className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium flex items-center space-x-1"
              >
                <span>Services</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Dropdown Menu - No gap, stays open on hover */}
              <div className={`absolute top-full left-0 pt-2 transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                <div className="w-56 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
                  <button
                    onClick={() => navigateToPage('/bmw-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>BMW Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/mercedes-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Mercedes Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/audi-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Audi Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/porsche-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Porsche Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/volkswagen-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Volkswagen Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/lamborghini-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Lamborghini Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/ferrari-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Ferrari Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/rollsroyce-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Rolls-Royce Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/bentley-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Bentley Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/landrover-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Land Rover Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/mclaren-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>McLaren Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/astonmartin-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Aston Martin Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => navigateToPage('/jaguar-repair-dubai')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Jaguar Repair Dubai</span>
                  </button>
                  <button
                    onClick={() => scrollToSection('services')}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-red-600 hover:text-white transition-colors duration-200 flex items-center space-x-2"
                  >
                    <Wrench className="w-4 h-4" />
                    <span>All Services</span>
                  </button>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium"
            >
              Contact
            </button>
            <Button
              onClick={() => {
                const message = "Hi Think Mechanic, I want to book a service and claim my 15% website discount!";
                const whatsappNumber = '+971566671468';
                const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-all duration-200 font-semibold"
            >
              Book Now
            </Button>
          </nav>

          {/* Mobile Menu Button - Red Square with White Lines */}
          <button
            className="md:hidden bg-red-600 text-white p-2.5 rounded transition-all duration-200 hover:bg-red-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium text-left"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium text-left"
              >
                About Us
              </button>
              
              {/* Services with Submenu */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium text-left w-full flex items-center justify-between"
                >
                  <span>Services</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Submenu */}
                {isServicesOpen && (
                  <div className="mt-2 ml-4 space-y-2">
                    <button
                      onClick={() => navigateToPage('/bmw-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>BMW Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/mercedes-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Mercedes Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/audi-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Audi Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/porsche-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Porsche Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/volkswagen-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Volkswagen Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/lamborghini-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Lamborghini Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/ferrari-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Ferrari Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/rollsroyce-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Rolls-Royce Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/bentley-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Bentley Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/landrover-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Land Rover Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/mclaren-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>McLaren Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/astonmartin-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Aston Martin Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => navigateToPage('/jaguar-repair-dubai')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>Jaguar Repair Dubai</span>
                    </button>
                    <button
                      onClick={() => scrollToSection('services')}
                      className="text-gray-600 hover:text-red-600 transition-colors duration-200 text-sm text-left w-full flex items-center space-x-2"
                    >
                      <Wrench className="w-3 h-3" />
                      <span>All Services</span>
                    </button>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-700 hover:text-red-600 transition-colors duration-200 font-medium text-left"
              >
                Contact
              </button>
              <Button
                onClick={() => {
                  const message = "Hi Think Mechanic, I want to book a service and claim my 15% website discount!";
                  const whatsappNumber = '+971566671468';
                  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/\s/g, '')}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                  setIsMobileMenuOpen(false);
                }}
                className="bg-red-600 hover:bg-red-700 text-white w-full py-2 rounded-lg transition-all duration-200 font-semibold"
              >
                Book Now
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
